import { computed, Signal, signal } from '@angular/core';

import { TTaskInstanceDetailResponse } from '@data-access/tasks';

import { CreateQuestionTodoRequest } from '../types';

/*
 * @NOTE: ERM-13220 — state shared between the per-question create-to-do trigger
 * in the internal questionnaire view and the Create Customer To-Do composer.
 *
 * The design does not put the composer in a modal: it opens inline at the foot
 * of the Collab Hub's To-do list, so the reviewer keeps the question and the
 * to-dos already raised in view while writing. The trigger and the composer
 * therefore sit in two different subtrees — the questionnaire content column
 * and the drawer — with no component between them to hold the request.
 *
 * It lives at module scope for the same reason the answer baselines
 * (question-answer-baseline.util) do: it must outlive either component being
 * destroyed and recreated, and neither owns it. Signals all the way out —
 * consumers read them or react in an `effect`; nothing subscribes.
 *
 * Memory only, and one request at a time: opening a second composer replaces
 * the first, which is exactly what the design does when the reviewer clicks a
 * different question's trigger.
 */
const _$request = signal<CreateQuestionTodoRequest | null>(null);
const _$lastCreatedTask = signal<TTaskInstanceDetailResponse | null>(null);
const _$createCount = signal(0);

/** The question the composer is anchored to, or null while it is closed. */
export const $questionTodoComposerRequest = _$request.asReadonly();

/**
 * The task the endpoint returned for the most recent create. The composer owns
 * its own toast; this exists so the questionnaire can patch the submission
 * state (OPEN FOR EDITS) that only its own facade can reach. Cleared whenever a
 * composer opens, so one create can never be read twice.
 */
export const $lastCreatedQuestionTodo = _$lastCreatedTask.asReadonly();

/**
 * How many to-dos have been created since load. Consumers react to a create
 * once by remembering this count: the task alone can't tell two creates apart,
 * because an effect never observes the null the composer writes in between.
 */
export const $questionTodoCreateCount = _$createCount.asReadonly();

/**
 * The request, but only while it belongs to the submission passed in.
 *
 * `WorkspaceLayoutComponent.ngOnDestroy` calls `closeQuestionTodoComposer()`
 * on every exit from a submission, so this filter is a defensive second layer
 * rather than the primary guard: the request carries its own `workspaceId`,
 * so even if that close were ever missed, a still-open request could not
 * reopen the composer against the wrong submission's To-do tab.
 */
export function questionTodoComposerRequestFor(
  $workspace: Signal<{ id?: string | null } | null | undefined>,
): Signal<CreateQuestionTodoRequest | null> {
  return computed(() => {
    const request = _$request();
    return request && request.workspaceId === $workspace()?.id ? request : null;
  });
}

/**
 * The question whose trigger renders in its composing state — the design fills
 * that question's icon for as long as the composer is open on it.
 */
export const $composingQuestionId = computed<string | null>(
  () => _$request()?.questionId ?? null,
);

/** Anchors the composer to a question, replacing any request already open. */
export function openQuestionTodoComposer(
  request: CreateQuestionTodoRequest,
): void {
  _$lastCreatedTask.set(null);
  _$request.set(request);
}

export function closeQuestionTodoComposer(): void {
  _$request.set(null);
}

/** Closes the composer and publishes the task that was created. */
export function completeQuestionTodoComposer(
  task: TTaskInstanceDetailResponse,
): void {
  _$request.set(null);
  _$lastCreatedTask.set(task);
  _$createCount.update((count) => count + 1);
}
